import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CouponApiService } from '@seatix/shared-data-access-coupon';
import { injectApiUrl } from '@seatix/shared-config';
import { withAuthRetry } from '@seatix/shared/data-access/auth';
import { map, Observable, retry, throwError, timer } from 'rxjs';
import { ApiResponse } from '@seatix/shared-domain-utils';
import {
  CheckoutErrorContext,
  CheckoutStep,
  MultiPaymentInitializeResponse,
  PaymentInitializeResponse,
  PaymentMethodType,
  PaymentStatus,
} from '@seatix/shared-domain-checkout';
import { CreateOrderData, Order } from '@seatix/shared-domain-order';
import { isTransientHttpError } from './order-confirmation-poll';

export interface PaymentStatusResponse {
  status: PaymentStatus;
  paymentId: string;
  lastUpdate: string;
  metadata?: Record<string, unknown>;
}

export interface OrderHoldExtensionResponse {
  success: boolean;
  newExpirationTime: string;
  extensionDuration: number;
}

export interface PaymentProcessResponse {
  success: boolean;
  transactionId?: string;
  status: PaymentStatus;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CheckoutApiService {
  #http = inject(HttpClient);
  #couponApi = inject(CouponApiService);
  #apiUrl = `${injectApiUrl()}/api`;

  createOrder(orderData: CreateOrderData): Observable<Order> {
    return this.#http
      .post<ApiResponse<Order>>(`${this.#apiUrl}/orders`, orderData, {
        context: withAuthRetry(
          'Tu sesión expiró. Inicia sesión para continuar con tu reserva.',
          'Reservando boletos...',
        ),
      })
      .pipe(
        map((response) => response.data),
        retry({
          count: 1,
          delay: 500,
          resetOnSuccess: true,
        }),
      );
  }

  /**
   * Get order details by orderId
   */
  getOrderByToken(token: string): Observable<Order> {
    return this.#http
      .get<ApiResponse<Order>>(`${this.#apiUrl}/orders/${token}`)
      .pipe(map((response) => response.data));
  }

  /**
   * Initialize payment for an order
   */
  initializePayment(
    orderId: number,
    paymentMethod: PaymentMethodType,
    redirectUrls?: { success_url: string; cancel_url: string },
  ): Observable<PaymentInitializeResponse> {
    const body: Record<string, unknown> = {
      payment_methods: [paymentMethod],
      ...redirectUrls,
    };

    return this.#http
      .post<ApiResponse<MultiPaymentInitializeResponse>>(
        `${this.#apiUrl}/orders/${orderId}/payment/initialize`,
        body,
        {
          context: withAuthRetry(
            'Tu sesión expiró. Inicia sesión para continuar con el pago.',
          ),
        },
      )
      .pipe(
        map((response) => {
          const multiResponse = response.data;
          const providers = Object.keys(multiResponse) as Array<
            keyof MultiPaymentInitializeResponse
          >;

          if (providers.length === 0) {
            throw new Error('No payment provider data received');
          }

          const providerKey = providers[0];
          const providerData = multiResponse[providerKey];

          if (!providerData) {
            throw new Error('Invalid payment provider data');
          }

          return {
            status: 'pending' as const,
            checkout_id: providerData.provider_checkout_id,
            provider_checkout_id: providerData.provider_checkout_id,
            provider_order_id: providerData.provider_order_id,
            methods: providerData.methods,
          } as PaymentInitializeResponse & {
            checkout_id: string;
            provider_checkout_id: string;
            provider_order_id: string;
            methods: string[];
          };
        }),
        retry({
          count: 1,
          delay: 500,
          resetOnSuccess: true,
        }),
      );
  }

  /**
   * Initialize multiple payment methods for an order
   */
  initializePaymentMethods(
    orderId: number,
    paymentMethods: PaymentMethodType[],
    redirectUrls?: { success_url: string; cancel_url: string },
  ): Observable<MultiPaymentInitializeResponse> {
    const body: Record<string, unknown> = {
      payment_methods: paymentMethods,
      ...redirectUrls,
    };

    return this.#http
      .post<ApiResponse<MultiPaymentInitializeResponse>>(
        `${this.#apiUrl}/orders/${orderId}/payment/initialize`,
        body,
        {
          context: withAuthRetry(
            'Tu sesión expiró. Inicia sesión para continuar con el pago.',
          ),
        },
      )
      .pipe(
        map((response) => response.data),
        retry({
          count: 1,
          delay: 500,
          resetOnSuccess: true,
        }),
      );
  }

  /**
   * Process payment with payment data
   */
  processPayment(
    orderId: number,
    paymentData: Record<string, unknown>,
  ): Observable<PaymentProcessResponse> {
    return this.#http
      .post<ApiResponse<PaymentProcessResponse>>(
        `${this.#apiUrl}/orders/${orderId}/payment/process`,
        paymentData,
      )
      .pipe(
        map((response) => response.data),
        // No retry for payment processing to avoid double charges
      );
  }

  /**
   * Confirm payment completion for an order
   */
  confirmPayment(orderId: number, paymentMethod: PaymentMethodType) {
    return this.#http
      .post<ApiResponse<{ order: Order }>>(
        `${this.#apiUrl}/orders/${orderId}/confirm`,
        {
          payment_method: paymentMethod,
        },
        {
          context: withAuthRetry(
            'Tu sesión expiró. Inicia sesión para confirmar tu pago.',
          ),
        },
      )
      .pipe(
        map((response) => response.data),
        retry({
          count: 2,
          // Only a dropped connection or a server error can answer differently
          // on a second try. Any 4xx is the API's verdict on the order — a 409
          // means the payment lock is held, a 400 that the order left `cart` —
          // and the outcome then comes from the order's status, so re-POSTing
          // only spends the confirm rate limit and delays the result.
          delay: (error: unknown) =>
            isTransientHttpError(error) ? timer(1000) : throwError(() => error),
          resetOnSuccess: true,
        }),
      );
  }

  /**
   * Extend order hold time to prevent expiration
   */
  extendOrderHold(orderId: number): Observable<OrderHoldExtensionResponse> {
    return this.#http
      .post<ApiResponse<OrderHoldExtensionResponse>>(
        `${this.#apiUrl}/orders/${orderId}/extend-hold`,
        {},
      )
      .pipe(
        map((response) => response.data),
        retry({
          count: 2,
          delay: 1000,
          resetOnSuccess: true,
        }),
      );
  }

  applyCoupon(
    orderId: number,
    code: string,
    referenceCode?: string,
  ): Observable<Order> {
    return this.#couponApi.applyCoupon(orderId, code, referenceCode);
  }

  removeCoupon(orderId: number): Observable<Order> {
    return this.#couponApi.removeCoupon(orderId);
  }

  /**
   * Get current payment status for polling
   */
  getPaymentStatus(paymentId: string): Observable<PaymentStatusResponse> {
    return this.#http
      .get<ApiResponse<PaymentStatusResponse>>(
        `${this.#apiUrl}/payments/${paymentId}/status`,
      )
      .pipe(
        map((response) => response.data),
        retry({
          count: 3,
          delay: 1000,
          resetOnSuccess: true,
        }),
      );
  }

  /**
   * Cancel an order explicitly
   */
  cancelOrder(orderId: number): Observable<{ success: boolean }> {
    return this.#http
      .post<ApiResponse<{ success: boolean }>>(
        `${this.#apiUrl}/orders/${orderId}/cancel`,
        {},
      )
      .pipe(
        map((response) => response.data),
        retry({
          count: 1,
          delay: 500,
          resetOnSuccess: true,
        }),
      );
  }

  /**
   * Release seats when user exits checkout
   */
  releaseSeats(
    orderId: number,
  ): Observable<Omit<ApiResponse<{ success: boolean }>, 'data'>> {
    const context: CheckoutErrorContext = {
      orderId,
      step: CheckoutStep.OrderReview,
    };

    return this.#http.post<Omit<ApiResponse<{ success: boolean }>, 'data'>>(
      `${this.#apiUrl}/orders/${orderId}/release`,
      {},
    );
  }
}
